import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { RefreshCw, Search, FileText, Eye, Phone, MapPin, Calendar, User, Heart, Pill, Shield, FileDown } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

const DetailRow = ({ icon: Icon, label, value, color = 'text-blue-600' }) => {
  if (!value || value === '—') return null;
  return (
    <div className="flex items-start gap-3 py-2.5 border-b border-slate-100 last:border-0">
      <Icon className={`w-4 h-4 mt-0.5 ${color} shrink-0`} />
      <div className="min-w-0">
        <p className="text-xs text-slate-400 uppercase tracking-wide">{label}</p>
        <p className="text-sm text-slate-800 font-medium mt-0.5">{value}</p>
      </div>
    </div>
  );
};

const JotFormPage = () => {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);

  const [configured, setConfigured] = useState(true);

  const fetchSubmissions = async () => {
    setLoading(true);
    try {
      const res = await api.get('/jotform/patient-submissions?limit=30');
      setSubmissions(res.data.submissions || []);
      setConfigured(res.data.configured !== false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load JotForm submissions');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSubmissions();
  }, []);

  const [pdfUrl, setPdfUrl] = useState(null);
  const [pdfOpen, setPdfOpen] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);

  const viewPatientPdf = async (e, submissionId) => {
    e.stopPropagation();
    setPdfLoading(true);
    setPdfOpen(true);
    try {
      const res = await api.get(`/jotform/patient-pdf/${submissionId}`, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      setPdfUrl(url);
    } catch {
      toast.error('Failed to load PDF');
      setPdfOpen(false);
    } finally {
      setPdfLoading(false);
    }
  };

  const closePdf = () => {
    setPdfOpen(false);
    if (pdfUrl) { URL.revokeObjectURL(pdfUrl); setPdfUrl(null); }
  };

  const openDetail = (sub) => {
    setSelectedPatient(sub);
    setDetailOpen(true);
  };

  const filtered = submissions.filter(s => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      s.patient_name?.toLowerCase().includes(term) ||
      s.date_of_birth?.includes(term) ||
      s.id?.includes(term)
    );
  });

  return (
    <div className="space-y-6" data-testid="jotform-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <FileText className="w-6 h-6 text-blue-600" />
            JotForm Submissions
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">Patient intake forms from JotForm</p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={fetchSubmissions}
          disabled={loading}
          data-testid="jotform-refresh"
        >
          <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {!configured && !loading ? (
        <Card className="border-amber-200 bg-amber-50" data-testid="jotform-not-configured">
          <CardContent className="p-6 text-center">
            <FileText className="w-8 h-8 text-amber-500 mx-auto mb-3" />
            <h3 className="text-base font-semibold text-slate-800">JotForm is not configured for this clinic</h3>
            <p className="text-sm text-slate-600 mt-1">
              Ask a Super Admin to add this clinic's JotForm API key and Form ID in the Clinics settings.
            </p>
          </CardContent>
        </Card>
      ) : (
      <Card className="border-slate-200">
        <CardContent className="p-4">
          <div className="flex items-center gap-2 mb-4">
            <Search className="w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search by name, DOB, or submission ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="max-w-sm"
              data-testid="jotform-search"
            />
            <span className="text-sm text-slate-500 ml-auto">{filtered.length} submission{filtered.length !== 1 ? 's' : ''}</span>
          </div>

          {loading ? (
            <div className="flex items-center justify-center h-40">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : (
            <div className="rounded-lg border border-slate-200 overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="text-xs uppercase tracking-wide text-slate-500 font-semibold w-12">#</TableHead>
                    <TableHead className="text-xs uppercase tracking-wide text-slate-500 font-semibold">Submission ID</TableHead>
                    <TableHead className="text-xs uppercase tracking-wide text-slate-500 font-semibold">Patient Name</TableHead>
                    <TableHead className="text-xs uppercase tracking-wide text-slate-500 font-semibold">Date of Birth</TableHead>
                    <TableHead className="text-xs uppercase tracking-wide text-slate-500 font-semibold">Submitted At</TableHead>
                    <TableHead className="text-xs uppercase tracking-wide text-slate-500 font-semibold w-28 text-center">PDF</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8 text-slate-400">
                        {searchTerm ? 'No matching submissions found' : 'No submissions available'}
                      </TableCell>
                    </TableRow>
                  ) : (
                    filtered.map((sub, idx) => (
                      <TableRow
                        key={sub.id}
                        className="hover:bg-blue-50/50 cursor-pointer transition-colors"
                        onClick={() => openDetail(sub)}
                        data-testid={`jotform-row-${idx}`}
                      >
                        <TableCell className="text-sm text-slate-400 font-mono">{idx + 1}</TableCell>
                        <TableCell className="text-sm text-slate-600 font-mono">{sub.id}</TableCell>
                        <TableCell className="text-sm font-medium text-slate-900">{sub.patient_name || '—'}</TableCell>
                        <TableCell className="text-sm text-slate-700">{sub.date_of_birth || '—'}</TableCell>
                        <TableCell className="text-sm text-slate-500">{sub.created_at || '—'}</TableCell>
                        <TableCell className="text-center">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => viewPatientPdf(e, sub.id)}
                            className="text-blue-600 hover:text-blue-800 hover:bg-blue-50"
                            data-testid={`jotform-pdf-${idx}`}
                          >
                            <FileDown className="w-4 h-4 mr-1" />
                            View PDF
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
      )}

      {/* Patient Detail Dialog */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="sm:max-w-3xl max-h-[85vh] overflow-y-auto p-0">
          {/* Header banner */}
          <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-5 rounded-t-lg">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center">
                <User className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-white">{selectedPatient?.patient_name || 'Patient'}</h2>
                <div className="flex items-center gap-4 mt-0.5 text-blue-100 text-sm">
                  {selectedPatient?.date_of_birth && <span>DOB: {selectedPatient.date_of_birth}</span>}
                  {selectedPatient?.sex && <span>{selectedPatient.sex}</span>}
                  {selectedPatient?.phone && <span>{selectedPatient.phone}</span>}
                </div>
              </div>
            </div>
          </div>

          {selectedPatient && (
            <div className="p-6">
              <div className="grid grid-cols-2 gap-4">
                {/* Left column */}
                <div className="space-y-4">
                  {/* Home Address */}
                  {selectedPatient.home_address && (
                    <div className="rounded-lg border border-slate-200 p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <MapPin className="w-4 h-4 text-red-500" />
                        <h3 className="text-xs uppercase tracking-wide text-slate-500 font-semibold">Home Address</h3>
                      </div>
                      <p className="text-sm text-slate-800">{selectedPatient.home_address}</p>
                    </div>
                  )}

                  {/* Medical */}
                  <div className="rounded-lg border border-slate-200 p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <Heart className="w-4 h-4 text-red-500" />
                      <h3 className="text-xs uppercase tracking-wide text-slate-500 font-semibold">Medical</h3>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <p className="text-[11px] text-slate-400 uppercase">Allergies</p>
                        <p className="text-sm text-slate-800 font-medium">{selectedPatient.allergies === 'No' ? 'None' : selectedPatient.allergies || 'None'}</p>
                      </div>
                      {selectedPatient.allergies_detail && selectedPatient.allergies_detail !== 'Ninguno' && (
                        <div>
                          <p className="text-[11px] text-slate-400 uppercase">Allergy Details</p>
                          <p className="text-sm text-slate-800 font-medium">{selectedPatient.allergies_detail}</p>
                        </div>
                      )}
                      <div>
                        <p className="text-[11px] text-slate-400 uppercase">Insurance</p>
                        <p className="text-sm text-slate-800 font-medium">{selectedPatient.insurance || 'None'}</p>
                      </div>
                      <div>
                        <p className="text-[11px] text-slate-400 uppercase">Dependents</p>
                        <p className="text-sm text-slate-800 font-medium">{selectedPatient.add_dependents || 'No'}</p>
                      </div>
                    </div>
                    {selectedPatient.medications && (
                      <div className="mt-3 pt-3 border-t border-slate-100">
                        <p className="text-[11px] text-slate-400 uppercase">Current Medications</p>
                        <p className="text-sm text-slate-800 font-medium">{selectedPatient.medications}</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Right column */}
                <div className="space-y-4">
                  {/* Pharmacy */}
                  {(selectedPatient.pharmacy_name || selectedPatient.pharmacy_address) && (
                    <div className="rounded-lg border border-slate-200 p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <Pill className="w-4 h-4 text-teal-600" />
                        <h3 className="text-xs uppercase tracking-wide text-slate-500 font-semibold">Pharmacy</h3>
                      </div>
                      {selectedPatient.pharmacy_name && <p className="text-sm text-slate-800 font-semibold">{selectedPatient.pharmacy_name}</p>}
                      {selectedPatient.pharmacy_address && <p className="text-sm text-slate-500 mt-0.5">{selectedPatient.pharmacy_address}</p>}
                    </div>
                  )}

                  {/* Referral & Submission */}
                  <div className="rounded-lg border border-slate-200 p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <FileText className="w-4 h-4 text-slate-500" />
                      <h3 className="text-xs uppercase tracking-wide text-slate-500 font-semibold">Other Info</h3>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      {selectedPatient.referral && (
                        <div>
                          <p className="text-[11px] text-slate-400 uppercase">Referral</p>
                          <p className="text-sm text-slate-800 font-medium">{selectedPatient.referral}</p>
                        </div>
                      )}
                      <div>
                        <p className="text-[11px] text-slate-400 uppercase">Submitted</p>
                        <p className="text-sm text-slate-800 font-medium">{selectedPatient.created_at || '—'}</p>
                      </div>
                    </div>
                    <div className="mt-3 pt-3 border-t border-slate-100">
                      <p className="text-[11px] text-slate-400 uppercase">Submission ID</p>
                      <p className="text-xs text-slate-500 font-mono">{selectedPatient.id}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* View PDF */}
              <div className="pt-3">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={(e) => viewPatientPdf(e, selectedPatient.id)}
                  className="text-blue-600 border-blue-200 hover:bg-blue-50"
                >
                  <FileDown className="w-4 h-4 mr-1" />
                  View PDF
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* PDF Preview Dialog */}
      <Dialog open={pdfOpen} onOpenChange={closePdf}>
        <DialogContent className="sm:max-w-4xl h-[90vh] flex flex-col p-0">
          <DialogHeader className="px-6 pt-5 pb-3 border-b border-slate-200 shrink-0">
            <DialogTitle className="text-lg font-bold text-slate-900 flex items-center gap-2">
              <FileDown className="w-5 h-5 text-blue-600" />
              Patient Form PDF
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 min-h-0">
            {pdfLoading ? (
              <div className="flex items-center justify-center h-full">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
              </div>
            ) : pdfUrl ? (
              <iframe
                src={pdfUrl}
                className="w-full h-full border-0"
                title="Patient PDF"
              />
            ) : null}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default JotFormPage;
